import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Subscriber } from '@atlaskit/rovo-triggers/main';
import { type Payload, Topics } from '@atlaskit/rovo-triggers/types';

import { useActiveAction } from '@atlassian/conversation-assistant/controllers/active-action';
import type { RovoChatRemixSession } from '@atlassian/conversation-assistant/ui/rovo-chat-remix-view';
import {
	type BaseConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';

import { expVal } from '@confluence/feature-experiments';
import { LoadableLazy } from '@confluence/loadable';

import { renderRovoChatRemixPicker } from './renderRovoChatRemixPicker.tsx';

const LazyRovoChatRemixView = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ConfluenceRovoChatRemixView" */ '@atlassian/conversation-assistant/ui/rovo-chat-remix-view'
			)
		).RovoChatRemixView,
});

type RovoRemixConversationAction = BaseConversationAction & {
	presentation: {
		hideActionHeader: true;
		suppressPromptLibrary: true;
		fillAvailableHeight: true;
	};
};

const isOpenRovoRemixSidebarPayload = (
	payload: Payload,
): payload is Extract<Payload, { type: 'open-rovo-remix-sidebar' }> =>
	payload.type === 'open-rovo-remix-sidebar';

const createRovoRemixAction = (session: RovoChatRemixSession): RovoRemixConversationAction => {
	return {
		id: `confluence-rovo-remix:${crypto.randomUUID()}`,
		title: 'Remix',
		handleType: HandleActionType.Base,
		icon: () => <React.Fragment />,
		presentation: {
			hideActionHeader: true,
			suppressPromptLibrary: true,
			fillAvailableHeight: true,
		},
		viewConfig: {
			render: () => (
				<LazyRovoChatRemixView
					renderRovoChatRemixPicker={renderRovoChatRemixPicker}
					session={session}
				/>
			),
		},
	};
};

export const RovoRemixActionLauncher = (): React.JSX.Element => {
	const { setActiveAction } = useActiveAction();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleEvent = useCallback(
		(payload: Payload) => {
			if (!isOpenRovoRemixSidebarPayload(payload)) {
				return;
			}
			// Variant2 Remix entry points converge here, ensuring one exposure per accepted launch.
			expVal('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control');
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'rovoChatRemixScreen',
					source: payload.source,
					attributes: {
						entryPoint: payload.source,
						product: 'confluence',
					},
				},
			}).fire();
			setActiveAction(
				createRovoRemixAction({
					consumer: payload.data.remixConsumer,
					scope: payload.data.remixPreset,
				}),
				{ preserveSelectedConversation: true },
			);
		},
		[createAnalyticsEvent, setActiveAction],
	);

	return (
		<Subscriber
			consumeOnceKey="confluence-rovo-remix-action-launcher"
			onEvent={handleEvent}
			topic={Topics.ROVO_REMIX_LAUNCH}
			triggerLatest
		/>
	);
};
